import React from "react";
import Dashboard from "../../../components/Dashboard";
import {
  COMPANY_STATUS_ACTIONS,
  COMPANY_STATUS_IDS,
  COMPANY_STATUS_LABELS,
  createSortByName,
  isCompanyActivated,
  isContractor,
  notifyError,
  PageHeight80,
  WizardTitle,
} from "../../../components/Utils";
import { FormMenu } from "../../../components/Buttons";

import BasicDataForm from "../components/wizard/BasicDataForm";
import GroupForm from "../components/wizard/GroupForm";
import TechKeyForm from "../components/wizard/TechKeyForm";
import RegionListForm from "../components/wizard/RegionListForm";
import DebitorListForm from "../components/wizard/DebitorListForm";
import ContractorListForm from "../components/wizard/ContractorListForm";

import { connect } from "react-redux";
import { frontloadConnect } from "react-frontload";
import {
  loadCompanyTypes,
  loadContractors,
  loadCompany,
  changeCompany,
  saveCompany,
  cancelCompany,
  exportSubCompanies,
} from "../actions";
import { loadKeys } from "../../Product/actions/keys";
import AdHocNewsRecipentListForm from "../components/wizard/AdHocNewsRecipientListForm";
import StatusForm from "../../../components/StatusForm";
import {
  PermissionResource,
  PermissionInPageNavMenu,
  Resources,
  PermissionSection,
  PermissionGroupFetcher,
} from "../../Permissions";
import FootprintForm from "../../../components/FootprintForm";
import { loadFootprints } from "../../News/actions";
import BasicDataFormHeader from "../components/wizard/BasicDataFormHeader";
import {
  isResourceEditable,
  isResourceReadable,
} from "../../Permissions/actions";
import ITDataForm from "../components/wizard/ITDataForm";

const frontload = async (props) => {
  const id = props.match.params.id;
  if (id === undefined || id === null) {
    return;
  }
  await props.getFootprints();
  await props.getCompanyTypes();
  await props.getContractors();
  await props.getTechkeys();
  await props.getCompany({ id, wizard: true });
};
const mapStateToProps = (state) => {
  return {
    permissions:
      state.permissions.permissionGroup["organisation.companywizard"],
    types: state.company.wizardTypes,
    techKeys: state.key.keys, // catalogKeys,
    userCompany: state.company.company,
    company: state.company.wizardCompany,
    companyWorkers: state.worker.companyWorkers,
    companies: state.company.contractors,
    footPrints: state.news.companyFootPrints,
  };
};
const mapDispatchToProps = (dispatch) => ({
  getFootprints: (...args) => dispatch(loadFootprints(...args)),
  getCompanyTypes: (...args) => dispatch(loadCompanyTypes(...args)),
  getContractors: (...args) => dispatch(loadContractors(...args)),
  getTechkeys: (...args) => dispatch(loadKeys(...args)),
  getCompany: (...args) => dispatch(loadCompany(...args)),
  changeCompany: (...args) => dispatch(changeCompany(...args)),
  saveCompany: (...args) => dispatch(saveCompany(...args)),
  cancelCompany: (...args) => dispatch(cancelCompany(...args)),
  exportSubCompanies: (...args) => dispatch(exportSubCompanies(...args)),
});

function CompanyWizard({
  className = "",
  types,
  techKeys,
  userCompany,
  company,
  companyWorkers,
  regions = ["1", "2", "3", "4", "7", "9"],
  companies = [],
  match,
  history,
  footPrints,
  permissions,
  changeCompany,
  saveCompany,
  cancelCompany,
  exportSubCompanies,
}) {
  const statusDataRef = React.useRef(null);
  const footPrintDataRef = React.useRef(null);
  const basicDataRef = React.useRef(null);
  const itDataRef = React.useRef(null);
  const groupDatenRef = React.useRef(null);
  const techKeysRef = React.useRef(null);
  const regionsRef = React.useRef(null);
  const debitorsRef = React.useRef(null);
  // the contract givers section is currently commented out; the ref stays
  // unassigned and the guard below keeps the previous behavior
  const contractGiversRef = React.useRef(null);
  const contractorsRef = React.useRef(null);
  const newsRecipientsRef = React.useRef(null);

  React.useEffect(() => {
    return () => {
      cancelCompany();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollIntoView = (element) => {
    if (!element) {
      return;
    }
    window.scrollTo(0, element.offsetTop);
  };

  const isCompanyEditable = () => {
    if (company && company.status_id === COMPANY_STATUS_IDS.DEACTIVATED) {
      return false;
    }
    const resourceKeys = Object.keys(Resources.Organisation.CompanyWizard);
    for (const key of resourceKeys) {
      const isWriteable = isResourceEditable(
        Resources.Organisation.CompanyWizard[key],
        permissions,
      );
      if (isWriteable) {
        return true;
      }
    }
    return false;
  };

  const getModel = (noValidate) => {
    const statusData = !statusDataRef.current
      ? null
      : statusDataRef.current.getModel();
    const footPrintData = !footPrintDataRef.current
      ? null
      : footPrintDataRef.current.getModel();
    let basicData = !basicDataRef.current
      ? null
      : basicDataRef.current.getModel(noValidate);

    // validate basic data and footPrint data before proceeding with merging the model
    if (
      !noValidate &&
      ((!basicData && basicDataRef.current) ||
        (!footPrintData && footPrintDataRef.current))
    ) {
      scrollIntoView(basicDataRef.current);
      notifyError({
        summary: "Firmenprofil",
        detail: `Die Firma konnte nicht gespeichert werden. Bitte Ihre Eingaben überprüfen.`,
      });
      return;
    }

    basicData = basicData || company;
    // it data
    const itData = !itDataRef.current
      ? {}
      : itDataRef.current.getModel(noValidate);
    basicData = {
      ...basicData,
      ...itData,
    };
    // groups
    const groups = !groupDatenRef.current
      ? company.groups
      : groupDatenRef.current.getModel(noValidate);
    // techKeys
    const companyTechKeys = !techKeysRef.current
      ? company.techKeys
      : techKeysRef.current.getModel(noValidate);
    // regions
    let companyRegions =
      (!regionsRef.current
        ? company.regions
        : regionsRef.current.getModel(noValidate)) || [];
    // debitors
    const debitors =
      (!debitorsRef.current
        ? null
        : debitorsRef.current.getModel(noValidate)) || [];
    if (companyRegions && companyRegions.length > 0 && debitors) {
      debitors.forEach((debitor) => {
        for (let index = 0; index < companyRegions.length; index++) {
          if (companyRegions[index].name === debitor.region) {
            if (companyRegions[index].debitors === undefined) {
              companyRegions[index].debitors = [];
            }
            companyRegions[index].debitors.push({ ...debitor });
          }
        }
      });
    }
    // contractGivers
    let contractGivers = basicData.contractGivers;
    if (contractGiversRef.current) {
      contractGivers = contractGiversRef.current.getModel();
    }
    // contractors
    let contractors = company.contractors;
    if (contractorsRef.current) {
      contractors = contractorsRef.current.getModel();
    }
    // ad-hoc news recipients
    let newsRecipients = company.newsRecipients;
    if (newsRecipientsRef.current) {
      newsRecipients = newsRecipientsRef.current.getModel();
      if (!newsRecipients) {
        notifyError({
          summary: "Firmenprofil",
          detail: `Die Firma konnte nicht gespeichert werden. Bitte Ihre Eingaben überprüfen.`,
        });
        return scrollIntoView(
          document.getElementById("adhoc-news-verteiler"),
        );
      }
    }
    const model = {
      ...basicData,
      groups,
      techKeys: companyTechKeys,
      regions: companyRegions,
      contractGivers,
      contractors,
      newsRecipients,
      status_id: statusData ? statusData.status_id : company.status_id,
      footPrint: footPrintData ? footPrintData.footPrint : company.footPrint,
    };
    if (match.params.id !== "neu" && company.id) {
      model.id = company.id;
    }
    return model;
  };
  const onSave = () => {
    const model = getModel();
    if (!model) return;
    const onSuccess = () => history.replace(`/organisation/firmen`);
    saveCompany(model, onSuccess);
  };
  const onCancel = () => {
    cancelCompany();
    history.replace(`/organisation/firmen`);
  };
  const onCompanyTypeChanged = (newType_id) => {
    const model = getModel(true);
    model.type_id = newType_id;
    changeCompany(model);
  };
  const onRegionsChanged = (newRegions) => {
    debitorsRef.current && debitorsRef.current.setRegions(newRegions);
  };
  const getRegionBaseData = () => {
    const basicData = !basicDataRef.current
      ? { type_id: company.type_id, address: null }
      : basicDataRef.current.getModel(true);
    return {
      getAddress: () => basicData,
      type_id: basicData.type_id,
    };
  };
  const onDebitorsChanged = (debitors) => {
    regionsRef.current && regionsRef.current.setDebitors(debitors);
  };
  const getDebitorBaseData = () => {
    const basicData = !basicDataRef.current
      ? { type_id: company.type_id } // { type_id: null }
      : basicDataRef.current.getModel(true);
    return {
      regions: regionsRef.current
        ? regionsRef.current.getModel()
        : [...company.regions],
      type_id: basicData.type_id,
    };
  };

  const renderMenu = (isCompanyContractor) => {
    let links = [
      {
        isVisible: true,
        to: "basisdaten",
        label: "Allgemeine Daten",
        resource: Resources.Organisation.CompanyWizard.BasicInfo,
      },
      {
        isVisible: true,
        to: "itdaten",
        label: "IT-Daten",
        resource: Resources.Organisation.CompanyWizard.ITData,
      },
      {
        isVisible: true,
        to: "gewerke",
        label: "Gewerke",
        resource: Resources.Organisation.CompanyWizard.Groups,
      },
    ];
    if (!isCompanyContractor) {
      links = links.concat([
        {
          isVisible: true,
          to: "technikerschluessel",
          label: "Technikerschlüssel",
          resource: Resources.Organisation.CompanyWizard.TechKeys,
        },
        {
          isVisible: true,
          to: "regionen",
          label: "Regionen",
          resource: Resources.Organisation.CompanyWizard.Regions,
        },
        {
          isVisible: true,
          to: "debitoren",
          label: "Debitoren",
          resource: Resources.Organisation.CompanyWizard.Debitors,
        },
      ]);
    }
    links.push({
      isVisible: true,
      to: "adhoc-news-verteiler",
      label: "Ad-hoc News Verteiler",
      resource: Resources.Organisation.CompanyWizard.AdHocNewsRecipents,
    });
    if (!isCompanyContractor) {
      links.push({
        isVisible: true,
        to: "subunternehmen",
        label: "Subunternehmen",
        resource: Resources.Organisation.CompanyWizard.Contractors,
      });
    }
    return (
      <PermissionInPageNavMenu className="under-wizard-title" links={links} />
    );
  };

  const id = match.params.id;
  if (!company || (id === "neu" && company.id != null) || !types) {
    return null;
  }
  const readOnly = !isCompanyEditable();
  const isCompanyContractor = isContractor(company);
  const isContractorsExportable = isResourceReadable(
    Resources.Organisation.CompanyWizard.ContractorsExport,
    permissions,
  );
  const Menu = renderMenu(isCompanyContractor);

  // debitors
  let debitors = [];
  company.regions &&
    company.regions.forEach((region) => {
      if (region.debitors && region.debitors.length > 0) {
        debitors = debitors.concat(region.debitors);
      }
    });
  if (company.changedDebitors) {
    const newDebitors = [];
    debitors.forEach((item) => {
      const found = company.changedDebitors.find(
        (debitor) => debitor.id === item.id,
      );
      if (!found) {
        newDebitors.push(item);
      }
    });
    debitors = company.changedDebitors
      .map((item) => ({
        ...item,
        readOnly: true,
      }))
      .concat(newDebitors);
  }

  // contractors
  const companyList = [...companies].sort(createSortByName());
  // remove company name from the list of possible contractors
  const contractorList = companyList.filter((item) => item.id !== company.id);

  return (
    <div className={`company-wizard clearfix ${className}`}>
      <PermissionGroupFetcher group="Organisation.CompanyWizard" />
      <WizardTitle
        type="company"
        model={company}
        menu={
          <FormMenu
            className="pad-big-top"
            onSave={onSave}
            onCancel={onCancel}
            saveBtnName="saveCompanyBtn2"
            cancelBtnName="cancelCompanyBtn2"
            saveHidden={!isCompanyEditable()}
            cancelBtnLabel={!isCompanyEditable() ? "Schließen" : undefined}
          />
        }
      />
      <div className="container">
        <Dashboard menu={Menu} menuSize={3}>
          <PageHeight80 className="">
            <PermissionResource
              resource={Resources.Organisation.CompanyWizard.BasicInfo}
            >
              <PermissionSection
                height="30"
                id="basisdaten"
                className="basicData w3-border margin-big-top pad-big"
                isUnterWizardTitle
              >
                <BasicDataFormHeader
                  title="Allgemeine Daten"
                  renderFootPrint={() => (
                    <FootprintForm
                      ref={footPrintDataRef}
                      className="footprint-form"
                      label="Footprint*"
                      model={{ footPrint: company.footPrint || "" }}
                      footPrints={footPrints}
                      disabled={readOnly}
                      required
                    />
                  )}
                  renderStatus={() => (
                    <PermissionResource
                      resource={Resources.Organisation.CompanyWizard.Status}
                    >
                      <StatusForm
                        ref={statusDataRef}
                        className="status-form"
                        model={{ status_id: company.status_id }}
                        statusIds={COMPANY_STATUS_IDS}
                        statusLabels={COMPANY_STATUS_LABELS}
                        statusActions={COMPANY_STATUS_ACTIONS}
                        isDisabled={
                          readOnly ||
                          (company.activeEmployees > 0 &&
                            isCompanyActivated(company))
                        }
                      />
                    </PermissionResource>
                  )}
                />
                <BasicDataForm
                  ref={basicDataRef}
                  model={company}
                  types={types}
                  userCompany={userCompany}
                  onCompanyTypeChanged={onCompanyTypeChanged}
                  onlyServicePartner
                  readOnly={readOnly}
                />
              </PermissionSection>
            </PermissionResource>
            <PermissionResource
              resource={Resources.Organisation.CompanyWizard.ITData}
            >
              <PermissionSection
                height="30"
                id="itdaten"
                className="groups w3-border margin-big-top pad-big"
                isUnterWizardTitle
              >
                <h3 className="no-margin-top">IT-Daten</h3>
                <ITDataForm
                  ref={itDataRef}
                  model={company}
                  readOnly={readOnly}
                />
              </PermissionSection>
            </PermissionResource>
            <PermissionResource
              resource={Resources.Organisation.CompanyWizard.Groups}
            >
              <PermissionSection
                height="30"
                id="gewerke"
                className="groups w3-border margin-big-top pad-big"
                isUnterWizardTitle
              >
                <h3 className="no-margin-top">Gewerke</h3>
                <GroupForm
                  ref={groupDatenRef}
                  model={company.groups}
                  readOnly={readOnly}
                />
              </PermissionSection>
            </PermissionResource>
            {!isCompanyContractor && (
              <>
                <PermissionResource
                  resource={Resources.Organisation.CompanyWizard.TechKeys}
                >
                  <PermissionSection
                    height="30"
                    id="technikerschluessel"
                    className="techKeys w3-border margin-big-top pad-big"
                    isUnterWizardTitle
                  >
                    <h3 className="no-margin-top">Technikerschlüssel</h3>
                    <TechKeyForm
                      ref={techKeysRef}
                      model={company.techKeys}
                      techKeys={techKeys}
                      readOnly={readOnly}
                    />
                  </PermissionSection>
                </PermissionResource>
                <PermissionResource
                  resource={Resources.Organisation.CompanyWizard.Regions}
                >
                  <PermissionSection
                    height="30"
                    id="regionen"
                    className="w3-border margin-big-top pad-big"
                    isUnterWizardTitle
                  >
                    <RegionListForm
                      className="regions"
                      ref={regionsRef}
                      model={company.regions}
                      regions={regions}
                      debitors={debitors}
                      getBaseData={getRegionBaseData}
                      onChange={onRegionsChanged}
                      // isCompanySpecialKDL={isCompanySpecialKDL}
                      readOnly={readOnly}
                    />
                  </PermissionSection>
                </PermissionResource>
                <PermissionResource
                  resource={Resources.Organisation.CompanyWizard.Debitors}
                >
                  <PermissionSection
                    height="30"
                    id="debitoren"
                    className="w3-border margin-big-top pad-big"
                    isUnterWizardTitle
                  >
                    <DebitorListForm
                      className="debitors"
                      ref={debitorsRef}
                      model={debitors}
                      regions={company.regions}
                      workers={companyWorkers}
                      getBaseData={getDebitorBaseData}
                      // isCompanySpecialKDL={isCompanySpecialKDL}
                      contractors={company.contractors}
                      company={company}
                      getCompany={() =>
                        basicDataRef.current
                          ? basicDataRef.current.getModel(true)
                          : company
                      }
                      onChange={onDebitorsChanged}
                      readOnly={readOnly}
                    />
                  </PermissionSection>
                </PermissionResource>
              </>
            )}
            {/* <PermissionResource
              resource={Resources.Organisation.CompanyWizard.ContractGiver}
              isVisible={isContractGiverVisible}
            >
              <PermissionSection
                id="auftraggeber"
                className="contractGivers w3-border margin-big-top pad-big"
                isUnterWizardTitle
              >
                <h3 className="no-margin-top">Auftraggeber</h3>
                <ContractGiverForm
                  ref={contractGiversRef}
                  model={company}
                  companies={companyList}
                  userCompany={userCompany}
                />
              </PermissionSection>
            </PermissionResource> */}
            <PermissionResource
              resource={Resources.Organisation.CompanyWizard.AdHocNewsRecipents}
            >
              <PermissionSection
                height="30"
                id="adhoc-news-verteiler"
                className="w3-border margin-big-top pad-big"
                isUnterWizardTitle
              >
                <AdHocNewsRecipentListForm
                  className="newsRecipients"
                  ref={newsRecipientsRef}
                  model={company.newsRecipients}
                  readOnly={readOnly}
                />
              </PermissionSection>
            </PermissionResource>
            {!isCompanyContractor && (
              <PermissionResource
                resource={Resources.Organisation.CompanyWizard.Contractors}
              >
                <PermissionSection
                  id="subunternehmen"
                  className="w3-border margin-big-top pad-big"
                  isUnterWizardTitle
                >
                  <ContractorListForm
                    className="contractors"
                    ref={contractorsRef}
                    model={company.contractors}
                    companies={contractorList}
                    getBaseData={() => ({
                      types,
                      getCompany: () => basicDataRef.current.getModel(true),
                    })}
                    exportSubCompanies={exportSubCompanies}
                    isExportable={isContractorsExportable}
                    readOnly={readOnly}
                  />
                </PermissionSection>
              </PermissionResource>
            )}
            <FormMenu
              className="w3-border margin-big-top pad-big w3-light-grey"
              onSave={onSave}
              onCancel={onCancel}
              saveBtnName="saveCompanyBtn"
              cancelBtnName="cancelCompanyBtn"
              saveHidden={!isCompanyEditable()}
              cancelBtnLabel={!isCompanyEditable() ? "Schließen" : undefined}
            />
          </PageHeight80>
        </Dashboard>
      </div>
    </div>
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  frontloadConnect(frontload, {
    onMount: true,
    onUpdate: false,
  })(CompanyWizard),
);
